// src/services/userPayment.service.ts
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  UserPayment,
  PaymentStatus,
  PaymentType,
  CreateUserPaymentDto,
  UpdateUserPaymentDto,
  UserPaymentSummary,
} from '../types/finance.types';
import { createExpenseFromStaffPayment, deleteExpensesBySource } from './expense.service';
import { User } from '../types/user.types';
import { FieldStaffPayment } from '../types/billing.types';

const COLLECTION_NAME = 'userPayments';

const generateId = () => `pmt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export async function createUserPayment(
  data: CreateUserPaymentDto,
  createdByUserId: string
): Promise<UserPayment> {
  try {
    const paymentId = generateId();
    const payment: UserPayment = {
      id: paymentId,
      userId: data.userId,
      userName: data.userName,
      userEmail: data.userEmail,
      amount: data.amount,
      paymentType: data.paymentType,
      description: data.description,
      projectId: data.projectId,
      projectName: data.projectName,
      periodStart: data.periodStart ? Timestamp.fromDate(data.periodStart) : undefined,
      periodEnd: data.periodEnd ? Timestamp.fromDate(data.periodEnd) : undefined,
      metrics: data.metrics,
      status: 'pending',
      scheduledDate: data.scheduledDate ? Timestamp.fromDate(data.scheduledDate) : undefined,
      externalReference: data.externalReference,
      paymentMethod: data.paymentMethod,
      notes: data.notes,
      createdAt: Timestamp.now(),
      createdBy: createdByUserId,
      updatedAt: Timestamp.now(),
    };

    await setDoc(doc(db, COLLECTION_NAME, paymentId), payment);
    return payment;
  } catch (error) {
    console.error('Error creating user payment:', error);
    throw error;
  }
}

export async function getUserPaymentById(paymentId: string): Promise<UserPayment | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, paymentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as UserPayment;
    }
    return null;
  } catch (error) {
    console.error('Error getting user payment:', error);
    throw error;
  }
}

export async function getUserPayments(filters?: {
  userId?: string;
  projectId?: string;
  status?: PaymentStatus;
  paymentType?: PaymentType;
  startDate?: Date;
  endDate?: Date;
}): Promise<UserPayment[]> {
  try {
    let q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));

    if (filters?.userId) {
      q = query(q, where('userId', '==', filters.userId));
    }
    if (filters?.projectId) {
      q = query(q, where('projectId', '==', filters.projectId));
    }
    if (filters?.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters?.paymentType) {
      q = query(q, where('paymentType', '==', filters.paymentType));
    }

    const snapshot = await getDocs(q);
    let payments = snapshot.docs.map((doc) => doc.data() as UserPayment);

    // Client-side date filtering
    if (filters?.startDate) {
      payments = payments.filter((pmt) => pmt.createdAt.toDate() >= filters.startDate!);
    }
    if (filters?.endDate) {
      payments = payments.filter((pmt) => pmt.createdAt.toDate() <= filters.endDate!);
    }

    return payments;
  } catch (error) {
    console.error('Error getting user payments:', error);
    throw error;
  }
}

export async function updateUserPayment(
  paymentId: string,
  data: UpdateUserPaymentDto
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, paymentId);
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (data.amount !== undefined) updateData.amount = data.amount;
    if (data.paymentType !== undefined) updateData.paymentType = data.paymentType;
    if (data.description !== undefined) updateData.description = data.description;
    if (data.status !== undefined) updateData.status = data.status;
    if (data.externalReference !== undefined) updateData.externalReference = data.externalReference;
    if (data.paymentMethod !== undefined) updateData.paymentMethod = data.paymentMethod;
    if (data.notes !== undefined) updateData.notes = data.notes;

    if (data.scheduledDate !== undefined) {
      updateData.scheduledDate = Timestamp.fromDate(data.scheduledDate);
    }
    if (data.paidDate !== undefined) {
      updateData.paidDate = Timestamp.fromDate(data.paidDate);
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating user payment:', error);
    throw error;
  }
}

export async function markPaymentAsPaid(
  paymentId: string,
  paidDate?: Date,
  externalReference?: string,
  paymentMethod?: string,
  createdByUserId?: string,
  createdByUserName?: string,
  paymentNotes?: string
): Promise<void> {
  try {
    // Get the payment first to create expense entry
    const payment = await getUserPaymentById(paymentId);
    if (!payment) {
      throw new Error('Payment not found');
    }

    const docRef = doc(db, COLLECTION_NAME, paymentId);
    const actualPaidDate = paidDate || new Date();

    await updateDoc(docRef, {
      status: 'paid',
      paidDate: Timestamp.fromDate(actualPaidDate),
      externalReference: externalReference || null,
      paymentMethod: paymentMethod || null,
      paidBy: createdByUserId || null,
      paidByName: createdByUserName || null,
      paymentNotes: paymentNotes || null,
      updatedAt: Timestamp.now(),
    });

    // Create expense entry for this staff payment
    try {
      await createExpenseFromStaffPayment(
        {
          description: payment.description,
          amount: payment.amount,
          projectId: payment.projectId,
          projectName: payment.projectName,
          date: actualPaidDate,
          staffUserId: payment.userId,
          staffUserName: payment.userName,
          paymentId: paymentId,
        },
        createdByUserId || payment.createdBy,
        createdByUserName || 'System'
      );
    } catch (expenseError) {
      console.error('Error creating expense from payment:', expenseError);
      // Don't throw - payment is still marked as paid
    }

  } catch (error) {
    console.error('Error marking payment as paid:', error);
    throw error;
  }
}

export async function cancelPayment(paymentId: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, paymentId);
    await updateDoc(docRef, {
      status: 'cancelled',
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error cancelling payment:', error);
    throw error;
  }
}

export async function deleteUserPayment(paymentId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, paymentId));
  } catch (error) {
    console.error('Error deleting user payment:', error);
    throw error;
  }
}

export async function getPaymentsByUser(userId: string): Promise<UserPayment[]> {
  return getUserPayments({ userId });
}

export async function getPaymentsByProject(projectId: string): Promise<UserPayment[]> {
  return getUserPayments({ projectId });
}

export async function getUserPaymentTotal(
  userId: string,
  status?: PaymentStatus
): Promise<number> {
  try {
    const payments = await getUserPayments({ userId, status });
    return payments.reduce((sum, pmt) => sum + pmt.amount, 0);
  } catch (error) {
    console.error('Error getting user payment total:', error);
    throw error;
  }
}

export async function getUserPaymentSummary(userId: string): Promise<UserPaymentSummary> {
  try {
    const payments = await getPaymentsByUser(userId);
    const user = payments[0]; // Get user info from first payment

    const totalPaid = payments
      .filter((p) => p.status === 'paid')
      .reduce((sum, p) => sum + p.amount, 0);

    const totalPending = payments
      .filter((p) => p.status === 'pending' || p.status === 'processing')
      .reduce((sum, p) => sum + p.amount, 0);

    // Group by payment type
    const typeMap = new Map<PaymentType, number>();
    payments
      .filter((p) => p.status === 'paid')
      .forEach((p) => {
        const existing = typeMap.get(p.paymentType) || 0;
        typeMap.set(p.paymentType, existing + p.amount);
      });

    // Group by project
    const projectMap = new Map<string, { projectId: string; projectName: string; amount: number }>();
    payments
      .filter((p) => p.status === 'paid' && p.projectId)
      .forEach((p) => {
        const existing = projectMap.get(p.projectId!);
        if (existing) {
          existing.amount += p.amount;
        } else {
          projectMap.set(p.projectId!, {
            projectId: p.projectId!,
            projectName: p.projectName || 'Unknown Project',
            amount: p.amount,
          });
        }
      });

    // Group by month
    const monthMap = new Map<string, number>();
    payments
      .filter((p) => p.status === 'paid' && p.paidDate)
      .forEach((p) => {
        const date = p.paidDate!.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const existing = monthMap.get(monthKey) || 0;
        monthMap.set(monthKey, existing + p.amount);
      });

    return {
      userId,
      userName: user?.userName || '',
      userEmail: user?.userEmail || '',
      totalPaid,
      totalPending,
      byPaymentType: Array.from(typeMap.entries()).map(([type, amount]) => ({ type, amount })),
      byProject: Array.from(projectMap.values()),
      byMonth: Array.from(monthMap.entries())
        .map(([month, amount]) => ({ month, amount }))
        .sort((a, b) => a.month.localeCompare(b.month)),
    };
  } catch (error) {
    console.error('Error getting user payment summary:', error);
    throw error;
  }
}

export async function getAllUserPaymentSummaries(): Promise<UserPaymentSummary[]> {
  try {
    const payments = await getUserPayments();

    // Group by user
    const userMap = new Map<string, UserPayment[]>();
    payments.forEach((p) => {
      const existing = userMap.get(p.userId) || [];
      existing.push(p);
      userMap.set(p.userId, existing);
    });

    const summaries: UserPaymentSummary[] = [];
    for (const [userId, userPayments] of userMap) {
      const totalPaid = userPayments
        .filter((p) => p.status === 'paid')
        .reduce((sum, p) => sum + p.amount, 0);

      const totalPending = userPayments
        .filter((p) => p.status === 'pending' || p.status === 'processing')
        .reduce((sum, p) => sum + p.amount, 0);

      summaries.push({
        userId,
        userName: userPayments[0]?.userName || '',
        userEmail: userPayments[0]?.userEmail || '',
        totalPaid,
        totalPending,
        byPaymentType: [],
        byProject: [],
        byMonth: [],
      });
    }

    return summaries.sort((a, b) => b.totalPaid - a.totalPaid);
  } catch (error) {
    console.error('Error getting all user payment summaries:', error);
    throw error;
  }
}

export async function getMonthlyPaymentBreakdown(
  year: number,
  projectId?: string
): Promise<{ month: number; monthName: string; total: number }[]> {
  try {
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31, 23, 59, 59);

    const payments = await getUserPayments({
      projectId,
      status: 'paid',
      startDate,
      endDate,
    });

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];

    const monthlyTotals = new Array(12).fill(0);

    payments.forEach((payment) => {
      const paidDate = payment.paidDate?.toDate();
      if (paidDate) {
        const month = paidDate.getMonth();
        monthlyTotals[month] += payment.amount;
      }
    });

    return monthlyTotals.map((total, index) => ({
      month: index + 1,
      monthName: monthNames[index],
      total,
    }));
  } catch (error) {
    console.error('Error getting monthly payment breakdown:', error);
    throw error;
  }
}

export async function getProjectStaffCosts(projectId: string): Promise<{
  totalPaid: number;
  totalPending: number;
  byUser: { userId: string; userName: string; amount: number }[];
  byType: { type: PaymentType; amount: number }[];
}> {
  try {
    const payments = await getPaymentsByProject(projectId);

    const totalPaid = payments
      .filter((p) => p.status === 'paid')
      .reduce((sum, p) => sum + p.amount, 0);

    const totalPending = payments
      .filter((p) => p.status === 'pending' || p.status === 'processing')
      .reduce((sum, p) => sum + p.amount, 0);

    // Group by user
    const userMap = new Map<string, { userId: string; userName: string; amount: number }>();
    payments
      .filter((p) => p.status === 'paid')
      .forEach((p) => {
        const existing = userMap.get(p.userId);
        if (existing) {
          existing.amount += p.amount;
        } else {
          userMap.set(p.userId, {
            userId: p.userId,
            userName: p.userName,
            amount: p.amount,
          });
        }
      });

    // Group by type
    const typeMap = new Map<PaymentType, number>();
    payments
      .filter((p) => p.status === 'paid')
      .forEach((p) => {
        const existing = typeMap.get(p.paymentType) || 0;
        typeMap.set(p.paymentType, existing + p.amount);
      });

    return {
      totalPaid,
      totalPending,
      byUser: Array.from(userMap.values()).sort((a, b) => b.amount - a.amount),
      byType: Array.from(typeMap.entries()).map(([type, amount]) => ({ type, amount })),
    };
  } catch (error) {
    console.error('Error getting project staff costs:', error);
    throw error;
  }
}

export async function getOrganizationPayments(organizationId: string): Promise<UserPayment[]> {
  try {
    // For now, get all payments - in production you'd filter by organization
    // This would require adding organizationId to UserPayment type
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as UserPayment);
  } catch (error) {
    console.error('Error getting organization payments:', error);
    throw error;
  }
}

export async function getAllPayments(): Promise<UserPayment[]> {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as UserPayment);
  } catch (error) {
    console.error('Error getting all payments:', error);
    throw error;
  }
}

// ============================================
// Year-End 1099 Package Generation
// ============================================

export interface ContractorYearEndSummary {
  user: User;
  totalPaid: number;
  userPaymentsTotal: number;
  fieldPaymentsTotal: number;
  w9Status: 'uploaded' | 'missing';
  w9Url?: string;
  w9UploadedAt?: Date;
  requires1099: boolean; // true if totalPaid >= $600
}

export async function generateYearEnd1099Package(year: number): Promise<ContractorYearEndSummary[]> {
  try {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31, 23, 59, 59);

    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const allUsers = usersSnapshot.docs.map(doc => doc.data() as User);

    // Get all paid userPayments for the year
    const userPaymentsQuery = query(
      collection(db, 'userPayments'),
      where('status', '==', 'paid'),
      orderBy('paidDate', 'desc')
    );
    const userPaymentsSnapshot = await getDocs(userPaymentsQuery);
    const allUserPayments = userPaymentsSnapshot.docs
      .map(doc => doc.data() as UserPayment)
      .filter(payment => {
        const paidDate = payment.paidDate?.toDate();
        return paidDate && paidDate >= yearStart && paidDate <= yearEnd;
      });

    // Get all paid fieldPayments for the year
    const fieldPaymentsQuery = query(
      collection(db, 'fieldPayments'),
      orderBy('weekEnd', 'desc')
    );
    const fieldPaymentsSnapshot = await getDocs(fieldPaymentsQuery);
    const allFieldPayments = fieldPaymentsSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as FieldStaffPayment))
      .filter(payment => {
        // Only include payments where weeklyPaymentPaid is true
        if (!payment.weeklyPaymentPaid) return false;

        const weekEnd = payment.weekEnd instanceof Date
          ? payment.weekEnd
          : (payment.weekEnd as any)?.toDate?.() || new Date(payment.weekEnd);
        return weekEnd >= yearStart && weekEnd <= yearEnd;
      });

    // Group payments by user
    const userPaymentsMap = new Map<string, number>();
    allUserPayments.forEach(payment => {
      const current = userPaymentsMap.get(payment.userId) || 0;
      userPaymentsMap.set(payment.userId, current + payment.amount);
    });

    const fieldPaymentsMap = new Map<string, number>();
    allFieldPayments.forEach(payment => {
      const current = fieldPaymentsMap.get(payment.userId) || 0;
      // Weekly payment due is the amount actually paid (50% of total earned)
      fieldPaymentsMap.set(payment.userId, current + payment.weeklyPaymentDue);

      // Also add holdback if released
      if (payment.holdbackReleased) {
        fieldPaymentsMap.set(payment.userId, (fieldPaymentsMap.get(payment.userId) || 0) + payment.holdbackAmount);
      }
    });

    // Get unique user IDs who received payments
    const paidUserIds = new Set([
      ...userPaymentsMap.keys(),
      ...fieldPaymentsMap.keys()
    ]);

    // Build contractor summaries
    const summaries: ContractorYearEndSummary[] = [];

    for (const userId of paidUserIds) {
      const user = allUsers.find(u => u.uid === userId);
      if (!user) continue;

      const userPaymentsTotal = userPaymentsMap.get(userId) || 0;
      const fieldPaymentsTotal = fieldPaymentsMap.get(userId) || 0;
      const totalPaid = userPaymentsTotal + fieldPaymentsTotal;

      summaries.push({
        user,
        totalPaid,
        userPaymentsTotal,
        fieldPaymentsTotal,
        w9Status: user.w9DocumentUrl ? 'uploaded' : 'missing',
        w9Url: user.w9DocumentUrl,
        w9UploadedAt: user.w9UploadedAt?.toDate(),
        requires1099: totalPaid >= 600
      });
    }

    // Sort by total paid descending
    return summaries.sort((a, b) => b.totalPaid - a.totalPaid);
  } catch (error) {
    console.error('Error generating year-end 1099 package:', error);
    throw error;
  }
}

export const userPaymentService = {
  createUserPayment,
  getUserPaymentById,
  getUserPayments,
  updateUserPayment,
  markPaymentAsPaid,
  cancelPayment,
  deleteUserPayment,
  getPaymentsByUser,
  getPaymentsByProject,
  getUserPaymentTotal,
  getUserPaymentSummary,
  getAllUserPaymentSummaries,
  getMonthlyPaymentBreakdown,
  getProjectStaffCosts,
  getOrganizationPayments,
  getAllPayments,
  generateYearEnd1099Package
};
